import { useEffect, useState } from "react";
import { DocumentPage, DocumentPagesResult, fetchDocumentPages } from "../../services/documentPagesService";

/**
 * The page pictures of a LinkedIn document post (SGL-573), for the review card.
 *
 * LinkedIn merges the post's PDFs, in order, into one document, so the pages run
 * through them in that order. A post shows pages only when every PDF has them:
 * half a document would misstate what publishes, so anything less is "error" and
 * the card keeps its PDF tile.
 */

export type DocumentPagesState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; pages: DocumentPage[]; totalPages: number };

/** The most pages shown, as the server draws at most this many per PDF. */
export const MAX_SHOWN_PAGES = 30;

/** Pages of every PDF in order, or null when any PDF has none. */
export const combineDocuments = (
  urls: string[],
  results: DocumentPagesResult[]
): { pages: DocumentPage[]; totalPages: number } | null => {
  const byUrl = new Map(results.map((r) => [r.url, r]));
  const pages: DocumentPage[] = [];
  let totalPages = 0;
  for (const url of urls) {
    const r = byUrl.get(url);
    if (!r || r.error || !r.pages?.length) return null;
    pages.push(...r.pages);
    totalPages += r.page_count ?? r.pages.length;
  }
  return { pages: pages.slice(0, MAX_SHOWN_PAGES), totalPages };
};

// One request per PDF set for the whole session: the card re-mounts as the
// review list pages and filters, and the pictures never change for a given PDF.
const cache = new Map<string, Promise<{ pages: DocumentPage[]; totalPages: number } | null>>();
const RETRY_AFTER_MS = 1500;

const load = async (customerId: string, urls: string[], idToken: string) => {
  let results = await fetchDocumentPages(customerId, urls, idToken);
  // "retry": the server ran out of time before these; the ones it drew are cached.
  if (results.some((r) => r.error === "retry")) {
    await new Promise((resolve) => setTimeout(resolve, RETRY_AFTER_MS));
    results = await fetchDocumentPages(customerId, urls, idToken);
  }
  return combineDocuments(urls, results);
};

/** Clears the session cache (tests). */
export const resetDocumentPagesCache = () => cache.clear();

export const useDocumentPages = (
  customerId: string | undefined,
  urls: string[],
  idToken: string | undefined
): DocumentPagesState | null => {
  const key = customerId && idToken && urls.length ? `${customerId}|${urls.join("|")}` : "";
  const [state, setState] = useState<DocumentPagesState | null>(key ? { status: "loading" } : null);

  useEffect(() => {
    if (!key || !customerId || !idToken) {
      setState(null);
      return;
    }
    let alive = true;
    let pending = cache.get(key);
    if (!pending) {
      pending = load(customerId, urls, idToken).catch(() => null);
      cache.set(key, pending);
    }
    setState({ status: "loading" });
    pending.then((doc) => {
      if (!alive) return;
      if (!doc) {
        // Not cached as a failure: the next view (or a fixed PDF) asks again.
        cache.delete(key);
        setState({ status: "error" });
      } else {
        setState({ status: "ready", ...doc });
      }
    });
    return () => {
      alive = false;
    };
    // `key` carries customerId and urls; idToken only authorises the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
};
